import React, { useEffect, useRef, useState } from "react";
import {
  ALERT_TITLES,
  ROUTES,
} from "../../../constants";
import {
  FormContainer,
  ErrorText,
  ButtonContainer,
  NextButton,
  BackButton,
  FlexContainer,
} from "../../../styles/PersonalInfoStep";
import {
  ContractText,
  SignatureCanvas,
  SignatureBox,
  ClearButton,
} from "../../../styles/SignContract";

import StyledCheckbox from "../../SignUp/steps/atoms/StyledCheckbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { alertMessage } from "../../../services/alertService";
import { GET, POST } from "../../../services/HTTPServices";
import { useNavigate } from "react-router-dom";
import {
  LoadingOverlay,
  LoadingSpinner,
  LoadingSpinnerIcon,
} from "../../../styles/loading";
import Back from "../../../assets/svgs/LoginSignup/Step1/back.svg";
import {
  FormSection,
  HeaderText,
  IllustrationContainer,
  LogoContainer,
  NormalText,
  PrimaryText,
  ProgressDot,
  ProgressIndicators,
  RegistrationContainer,
  SignupWrapper,
  StepperDescription,
  StepperSection,
  StepperTextContent,
  StepperTitle,
  StepperWrapper,
  Typography,
} from "../../../styles/merchantRegistration";
import SignUpImage1 from "../../../assets/svgs/LoginSignup/MerchantSignUp1.svg";
import SignUpImage2 from "../../../assets/svgs/LoginSignup/MerchantSignUp2.svg";
import SignUpImage3 from "../../../assets/svgs/LoginSignup/MerchantSignUp3.svg";
import Logo from "../../../assets/svgs/LoginSignup/logo.svg";

const contractText = `Aliquam nec orci in sem rhoncus tempor. Quisque id metus nec tellus porttitor porttitor. Suspendisse potenti. Pellentesque fringilla porttitor tellus, id convallis sem vestibulum nec. Nullam pulvinar felis non tellus fermentum, nec dapibus arcu tincidunt. Integer nec tellus diam. Donec quis nibh ut libero hendrerit convallis.\n\nDonec consequat nunc ac turpis imperdiet, sed ultricies augue gravida. Sed tincidunt orci eu urna facilisis, a interdum odio feugiat. Vivamus sed turpis nibh. Aenean aliquam nibh id metus imperdiet dapibus. Pellentesque vitae nulla eget mauris ullamcorper finibus. Etiam cursus tincidunt nulla, non varius sapien tincidunt ac.`;

const ProfileSignContract: React.FC = () => {
  const navigate = useNavigate();

  const [agreed, setAgreed] = useState(false);
  const [signature, setSignature] = useState("");

  const [serviceProviderId, setServiceProviderId] = useState("");
  const [hasDrawn, setHasDrawn] = useState(false); // Track if user has drawn anything

  const [error, setError] = useState("");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [loading, setLoading] = React.useState(false);
  const [currentLeftStep, setCurrentLeftStep] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentLeftStep((prevIndex) => (prevIndex === 2 ? 0 : prevIndex + 1));
    }, 3000);

    return () => clearTimeout(timer); // Cleanup function
  }, [currentLeftStep]);

  const steps = [
    {
      title: "Easy Order",
      SubTitle: "Management",
      description:
        "Manage your order and delivery smoothly with our simple app.",
      image: SignUpImage1,
    },
    {
      title: "Promote Your",
      SubTitle: "Business",
      description:
        "Let everyone know about your restaurant and special deals through our app.",
      image: SignUpImage2,
    },
    {
      title: "Reach More",
      SubTitle: "Customers",
      description:
        "Get your food business seen by more people through our app.",
      image: SignUpImage3,
    },
  ];

  const currentStepData = steps[currentLeftStep];

  useEffect(() => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
    if (authUser) {
      setServiceProviderId(authUser.id + "");
    } else {
      const serviceProviderId = localStorage.getItem("serviceProviderId") || "";
      if (serviceProviderId) {
        setServiceProviderId(serviceProviderId);
      }
    }
  }, []);

  // Check if canvas is blank (empty)
  const isCanvasBlank = (canvas: HTMLCanvasElement): boolean => {
    const context = canvas.getContext("2d", { willReadFrequently: true });

    if (!context) return true;

    const pixelBuffer = new Uint32Array(
      context.getImageData(0, 0, canvas.width, canvas.height).data.buffer
    );

    return !pixelBuffer.some((color) => color !== 0);
  };

  // Handle drawing signature
  const handleMouseDown = (e: React.MouseEvent) => {
    setDrawing(true);
    setHasDrawn(true); // Mark that user has started drawing
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (ctx) {
        ctx.beginPath();
        ctx.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!drawing) return;
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (ctx) {
        ctx.lineTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
        ctx.stroke();
      }
    }
  };

  const handleMouseUp = () => {
    setDrawing(false);
    saveSignature();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setDrawing(true);
    setHasDrawn(true); // Mark that user has started drawing
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0];
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (ctx) {
        ctx.beginPath();
        ctx.moveTo(touch.clientX - rect.left, touch.clientY - rect.top);
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!drawing) return;
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0];
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (ctx) {
        ctx.lineTo(touch.clientX - rect.left, touch.clientY - rect.top);
        ctx.stroke();
      }
    }
  };

  const handleTouchEnd = () => {
    setDrawing(false);
    saveSignature();
  };

  // Clear signature
  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
    setSignature("");
    setHasDrawn(false); // Reset the drawing state
  };

  // Save signature as dataURL only if something was drawn
  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (canvas && hasDrawn && !isCanvasBlank(canvas)) {
      setSignature(canvas.toDataURL());
    } else if (canvas && isCanvasBlank(canvas)) {
      setSignature(""); // Clear signature if canvas is blank
    }
  };

  // Form submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Check if signature exists and is not empty
    if (!signature || signature === "" || !hasDrawn) {
      setError("Please provide your signature.");
      return;
    }

    // Double-check if canvas is actually blank
    const canvas = canvasRef.current;
    if (canvas && isCanvasBlank(canvas)) {
      setError("Please provide your signature.");
      return;
    }

    if (!agreed) {
      setError("Please agree to the Terms & Conditions and Privacy Policy.");
      return;
    }

    postSignature();
  };

  const postSignature = async () => {
    setLoading(true);

    let url = "serviceprovider/SignPdfByServiceProvider";

    const base64Data = signature.split(",")[1];

    let temp = {
      serviceProviderId: serviceProviderId + "" || "",
      eSignPhotoPath: base64Data,
    };

    let res: any = await POST(url, temp, "provider");

    if (res && res.status === 200) {
      getCheckList(serviceProviderId);
    } else {
      setLoading(false);
      if (res.status && res.status === 401) navigate("/unauthorized-page");
      else {
        alertMessage(ALERT_TITLES.ERROR, res.message);
        setError(res.message);
      }
    }
  };

  const getCheckList = async (id: any) => {
    const res: any = await GET(
      "onboardingcheck/GetOnboardingCheckBySpId?spId=" + id,
      "provider"
    );
    if (res && res.data) {
      postChecklist(res.data[0]);
    }
  };

  const postChecklist = async (checklist: any) => {
    setLoading(true);
    let url = "onboardingcheck/UpdateOnboardingCheck";

    let temp = {
      serviceProviderId: checklist.serviceProviderId,
      personalInfo: checklist.personalInfo,
      businessInfo: checklist.businessInfo,
      kyb: checklist.kyb,
      fileUpload: checklist.fileUpload,
      agreement: 2,
    };

    let res: any = await POST(url, temp, "provider");

    if (res && res.status === 200) {
      alertMessage(ALERT_TITLES.SUCCESS, "You signed successfully.");
      navigate(ROUTES.DASHBOARD);
      setLoading(false);
    } else {
      setLoading(false);
      if (res.status && res.status === 401) navigate("/unauthorized-page");
      else {
        alertMessage(ALERT_TITLES.ERROR, res.message);
      }
    }
  };


  const displayText = () => {
    switch (currentLeftStep) {
      case 0: {
        return (
          <>
            <HeaderText>
              <NormalText>Reach More </NormalText>
              <PrimaryText>Customers</PrimaryText>
            </HeaderText>
            <Typography>
              Get your food business seen by more people through our app.
            </Typography>
          </>
        );
      }
      case 1: {
        return (
          <>
            <HeaderText>
              <NormalText>Easy </NormalText>
              <PrimaryText>Order </PrimaryText>
              <NormalText>Management</NormalText>
            </HeaderText>
            <Typography>
              Manage your order and delivery smoothly with our simple app.
            </Typography>
          </>
        );
      }
      case 2: {
        return (
          <>
            <HeaderText>
              <NormalText>Promote Your </NormalText>
              <PrimaryText>Business</PrimaryText>
            </HeaderText>
            <Typography>
              Let everyone know about your restaurant and special deals through
              our app.{" "}
            </Typography>
          </>
        );
      }
    }
  };

  return (
    <>
      {loading && (
        <LoadingOverlay>
          <LoadingSpinner>
            <LoadingSpinnerIcon />
          </LoadingSpinner>
        </LoadingOverlay>
      )}

      <SignupWrapper>
        <RegistrationContainer $isSidebarCollapsed={false}>
          {/* Left side - Stepper with illustrations */}
          <StepperWrapper>
            <StepperSection>
              <LogoContainer>
                <img src={Logo} alt="Logo" />
              </LogoContainer>

              <IllustrationContainer>
                <img
                  src={currentStepData.image}
                  alt="Registration illustration"
                />
              </IllustrationContainer>

              <StepperTextContent>
                <StepperTitle>{displayText()}</StepperTitle>
                <StepperDescription>
                  {currentStepData.description}
                </StepperDescription>
              </StepperTextContent>

              <ProgressIndicators>
                {steps.map((_, index) => (
                  <ProgressDot
                    key={index}
                    $active={index === currentLeftStep}
                  />
                ))}
              </ProgressIndicators>
            </StepperSection>
          </StepperWrapper>

          {/* Right side - Form content */}
          <FormSection>
            {" "}
            <FormContainer>
              <ContractText>{contractText}</ContractText>
              <SignatureBox>
                <SignatureCanvas
                  ref={canvasRef}
                  height={180}
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                />
              </SignatureBox>
              <ClearButton type="button" onClick={clearSignature}>
                Clear Signature
              </ClearButton>
              <form onSubmit={handleSubmit}>
                <FormControlLabel
                  control={
                    <StyledCheckbox
                      checked={agreed}
                      onChange={(e : any) => setAgreed(e.target.checked)}
                      inputProps={{ "aria-label": "agreement checkbox" }}
                    />
                  }
                  label="I have read and agree to the Terms & Conditions and Privacy Policy"
                />
                {error && <ErrorText>{error}</ErrorText>}
                <ButtonContainer>
                  <BackButton
                    type="button"
                    onClick={() => navigate(ROUTES.DASHBOARD)}
                  >
                    <img src={Back} alt="Back" />
                  </BackButton>
                  <FlexContainer>
                    <NextButton type="submit">Submit</NextButton>
                  </FlexContainer>
                </ButtonContainer>
              </form>
            </FormContainer>
          </FormSection>
        </RegistrationContainer>
      </SignupWrapper>
    </>
  );
};

export default ProfileSignContract;
